import React from 'react';
import {
    RawPurchaseEntryBase,
    buildRawPurchaseEntryDetailPanelTypes,
    buildRawPurchaseEntryHiddenPanelTypes,
    buildRawPurchaseEntryTotalPanelDefinitions,
    RAW_PURCHASE_ENTRY_FIELD_IDS,
    applyRawPurchaseEntryReferenceHeaderLocks,
    hasRawPurchaseEntryReference
} from './RawPurchaseEntryBase';
import { DeepCopy, DicVal, GetDesitRound, GetRound, NumConv, isEmpty } from '../modules/appLibrary';

const HEADER_LOCK_COLUMN_IDS = [
    "ZSR_ZAIRYOKBN",
    "ZSR_SIIREKIND",
    "ZSR_DENSYUBETU",
    "ZSR_DENKBN",
    "ZSR_SHIREBI",
    "ZSR_SENPODENNO",
    "ZSR_BRDCD",
    "ZSR_SIIRESAKIKBN",
    "ZSR_SIIRESAKICD",
    "ZSR_SIIRESAKISUBCD",
    "TOR_BUMONNM",
    "TOR_TANTONM",
    "ZSR_KAIGAIKBN",
    "ZSR_TEKIYO"
];

const HEADER_FIELD_IDS = {
    kaigaiKbn: "ZSR_KAIGAIKBN",
    shanaiRate: "ZSR_SHANAIRATE",
    siireSakiKbn: "ZSR_SIIRESAKIKBN",
    siireSakiCd: "ZSR_SIIRESAKICD",
    siireSakiSubCd: "ZSR_SIIRESAKISUBCD"
};

const DETAIL_PANEL_TYPES = {
    material: "310",
    accessory: "320",
    subMaterial: "330"
};

const DETAIL_COLUMN_IDS = {
    zairyoKbn: "ZSRM_ZAIRYOKBN",
    gyono: "ZSRM_GYONO",
    ssdNo: "ZSRM_SSDNO",
    hinban: "ZSRM_ZAIHINBAN",
    zaiSiireSakiKbn: "ZSRM_ZAISIIRESAKIKBN",
    zaiSiireSakiCd: "ZSRM_ZAISIIRESAKICD",
    zaiSiireSakiSubCd: "ZSRM_ZAISIIRESAKISUBCD",
    zaiColorCd: "ZSRM_ZAICOLORCD",
    mekerColorNo: "ZSRM_MEKERCOLORNO",
    tanka: "ZSRM_TANKA",
    tankaF: "ZSRM_TANKAF",
    meterTanka: "ZSRM_METERTANKA",
    meterTankaF: "ZSRM_METERTANKAF",
    siireKingaku: "ZSRM_SIIREKINGAKU",
    siireKingakuF: "ZSRM_SIIREKINGAKUF",
    kingakuZ: "ZSRM_KINGAKUZ",
    siireTanSu: "ZSRM_SIIRETANSU",
    siireMeterSu: "ZSRM_SIIREMETERSU",
    siireSu: "ZSRM_SIIRESU",
    cutCostHinban: "CutCostHinban",
    cutCostSiireSakiCd: "CutCostSiireSakiCd",
    cutCostBrdCd: "CutCostBrdCd"
};

const CUT_COST_FIXED_COLUMN_IDS = [
    DETAIL_COLUMN_IDS.hinban
];

const CUT_COST_MANUAL_UNIT_PRICE_COLUMN_IDS = [
    DETAIL_COLUMN_IDS.tanka,
    DETAIL_COLUMN_IDS.tankaF
];

function normalizedValue(value) {
    return String(value ?? "").trim();
}

function panelType(panelInfo) {
    return normalizedValue(DicVal(panelInfo, "type", ""));
}

function resolveColumnId(context) {
    return normalizedValue(context?.params?.colDef?.field ?? context?.params?.column?.colId ?? "");
}

function resolveCutCostContext(context) {
    const detailPanels = Array.isArray(context.DetailGridList?.()) ? context.DetailGridList() : [];
    const panelRows = [];
    detailPanels.forEach(detailPanelInfo => {
        const rows = context.GridData(detailPanelInfo);
        if (!Array.isArray(rows)) return;
        rows.forEach(row => panelRows.push(row));
    });

    const sourceRow = panelRows.find(row =>
        !isEmpty(DicVal(row, DETAIL_COLUMN_IDS.cutCostHinban, ""))
        || !isEmpty(DicVal(row, DETAIL_COLUMN_IDS.cutCostSiireSakiCd, ""))
        || !isEmpty(DicVal(row, DETAIL_COLUMN_IDS.cutCostBrdCd, ""))
    );
    if (isEmpty(sourceRow)) return null;

    const cutCostHinban = normalizedValue(DicVal(sourceRow, DETAIL_COLUMN_IDS.cutCostHinban, ""));
    const cutCostSiireSakiCd = normalizedValue(DicVal(sourceRow, DETAIL_COLUMN_IDS.cutCostSiireSakiCd, ""));
    const cutCostBrdCd = normalizedValue(DicVal(sourceRow, DETAIL_COLUMN_IDS.cutCostBrdCd, ""));
    if (cutCostHinban === "") {
        return null;
    }

    return {
        cutCostHinban,
        cutCostSiireSakiCd,
        cutCostBrdCd
    };
}

function isCutCostRow(row, cutCostContext) {
    if (isEmpty(row)) return false;
    const rowCutCostHinban = normalizedValue(DicVal(row, DETAIL_COLUMN_IDS.cutCostHinban, ""));
    const targetCutCostHinban = rowCutCostHinban !== ""
        ? rowCutCostHinban
        : normalizedValue(DicVal(cutCostContext, "cutCostHinban", ""));
    if (targetCutCostHinban === "") return false;
    return normalizedValue(DicVal(row, DETAIL_COLUMN_IDS.hinban, "")) === targetCutCostHinban;
}

function quantityColumnIds(currentPanelType) {
    if (currentPanelType === DETAIL_PANEL_TYPES.material) {
        return [DETAIL_COLUMN_IDS.siireTanSu, DETAIL_COLUMN_IDS.siireMeterSu];
    }
    if (currentPanelType === DETAIL_PANEL_TYPES.accessory || currentPanelType === DETAIL_PANEL_TYPES.subMaterial) {
        return [DETAIL_COLUMN_IDS.siireSu];
    }
    return [];
}

function canEditReferencedRow(context) {
    const currentPanelType = panelType(context.panelInfo);
    const cutCostContext = resolveCutCostContext(context);
    const row = context?.params?.data;
    const columnId = resolveColumnId(context);
    const currentQuantityColumnIds = quantityColumnIds(currentPanelType);
    const hasReferenceSlipNo = hasRawPurchaseEntryReference(context);
    const hasReferenceRowKeys = normalizedValue(DicVal(row, DETAIL_COLUMN_IDS.gyono, "")) !== ""
        || normalizedValue(DicVal(row, DETAIL_COLUMN_IDS.ssdNo, "")) !== ""
        || isCutCostRow(row, cutCostContext);
    if (!hasReferenceSlipNo && !hasReferenceRowKeys) return context.edit;

    if (currentQuantityColumnIds.length !== 0) {
        if (isCutCostRow(row, cutCostContext)) {
            if (CUT_COST_FIXED_COLUMN_IDS.includes(columnId)) return false;
            if (CUT_COST_MANUAL_UNIT_PRICE_COLUMN_IDS.includes(columnId)) return true;
            if (currentQuantityColumnIds.includes(columnId)) return context.edit;
            return false;
        }
        if (currentQuantityColumnIds.includes(columnId)) return context.edit;
        return false;
    }
    return false;
}

function recalculateReferenceDetailRows(context, panelInfo) {
    const currentPanelType = panelType(panelInfo);
    const currentQuantityColumnIds = quantityColumnIds(currentPanelType);
    const currentRows = context.GridData(panelInfo);
    if (currentQuantityColumnIds.length === 0 || !Array.isArray(currentRows) || currentRows.length === 0) {
        return false;
    }

    const entryData = isEmpty(context.entrydataRef.current) ? {} : context.entrydataRef.current;
    const isForeign = normalizedValue(DicVal(entryData, HEADER_FIELD_IDS.kaigaiKbn, "")) === "2";
    const rate = NumConv(DicVal(entryData, HEADER_FIELD_IDS.shanaiRate, 0));
    const cutCostContext = resolveCutCostContext(context);
    const nextRows = DeepCopy(currentRows);
    let changed = false;

    nextRows.forEach(row => {
        if (isEmpty(row)) return;

        let tanka = NumConv(DicVal(row, DETAIL_COLUMN_IDS.tanka, 0));
        let tankaF = NumConv(DicVal(row, DETAIL_COLUMN_IDS.tankaF, 0));
        const qty = currentPanelType === DETAIL_PANEL_TYPES.material
            ? NumConv(DicVal(row, DETAIL_COLUMN_IDS.siireMeterSu, 0))
            : NumConv(DicVal(row, DETAIL_COLUMN_IDS.siireSu, 0));

        if (isCutCostRow(row, cutCostContext)) {
            // 画面設計書では出荷実績参照で追加したカット代は手入力運用です。
            // この行だけは為替換算の再計算を行わず、入力された単価をそのまま保持します。
            const kingaku = GetRound(qty * tanka, 0);
            const kingakuF = isForeign ? GetDesitRound(qty * tankaF, 0, 2) : NumConv(0);

            if (currentPanelType === DETAIL_PANEL_TYPES.material) {
                if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.meterTanka, 0)) !== tanka) {
                    row[DETAIL_COLUMN_IDS.meterTanka] = tanka;
                    changed = true;
                }
                if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.meterTankaF, 0)) !== tankaF) {
                    row[DETAIL_COLUMN_IDS.meterTankaF] = tankaF;
                    changed = true;
                }
            }

            if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.siireKingaku, 0)) !== kingaku) {
                row[DETAIL_COLUMN_IDS.siireKingaku] = kingaku;
                changed = true;
            }
            if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.siireKingakuF, 0)) !== kingakuF) {
                row[DETAIL_COLUMN_IDS.siireKingakuF] = kingakuF;
                changed = true;
            }
            if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.kingakuZ, 0)) !== kingaku) {
                row[DETAIL_COLUMN_IDS.kingakuZ] = kingaku;
                changed = true;
            }
            return;
        }

        if (isForeign && rate !== 0) {
            if (tankaF !== 0 || tanka === 0) {
                tanka = GetRound(rate * tankaF, 1);
            } else {
                tankaF = GetDesitRound(tanka / rate, 1, 2);
            }
        } else {
            tankaF = NumConv(0);
        }

        const kingaku = GetRound(qty * tanka, 0);
        const kingakuF = isForeign ? GetDesitRound(qty * tankaF, 0, 2) : NumConv(0);

        if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.tanka, 0)) !== tanka) {
            row[DETAIL_COLUMN_IDS.tanka] = tanka;
            changed = true;
        }
        if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.tankaF, 0)) !== tankaF) {
            row[DETAIL_COLUMN_IDS.tankaF] = tankaF;
            changed = true;
        }
        if (currentPanelType === DETAIL_PANEL_TYPES.material) {
            if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.meterTanka, 0)) !== tanka) {
                row[DETAIL_COLUMN_IDS.meterTanka] = tanka;
                changed = true;
            }
            if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.meterTankaF, 0)) !== tankaF) {
                row[DETAIL_COLUMN_IDS.meterTankaF] = tankaF;
                changed = true;
            }
        }
        if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.siireKingaku, 0)) !== kingaku) {
            row[DETAIL_COLUMN_IDS.siireKingaku] = kingaku;
            changed = true;
        }
        if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.siireKingakuF, 0)) !== kingakuF) {
            row[DETAIL_COLUMN_IDS.siireKingakuF] = kingakuF;
            changed = true;
        }
        if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.kingakuZ, 0)) !== kingaku) {
            row[DETAIL_COLUMN_IDS.kingakuZ] = kingaku;
            changed = true;
        }
    });

    if (!changed) return false;

    context.ApplyGridData(panelInfo, nextRows);
    context.SyncTotalPanel(panelInfo, nextRows, null);
    return true;
}

function scheduleNormalizeAddedCutCostRows(context, beforeRowIds, cutCostContext) {
    window.setTimeout(() => {
        const currentPanelInfo = context.panelInfo;
        const currentRows = context.GridData(currentPanelInfo);
        if (!Array.isArray(currentRows) || currentRows.length === 0) {
            return;
        }

        const existingRowIds = new Set(Array.isArray(beforeRowIds) ? beforeRowIds : []);
        const nextRows = DeepCopy(currentRows);
        const changedRowIds = [];
        const currentPanelType = panelType(currentPanelInfo);
        const entryData = isEmpty(context.entrydataRef.current) ? {} : context.entrydataRef.current;
        const headerSiireSakiKbn = normalizedValue(DicVal(entryData, HEADER_FIELD_IDS.siireSakiKbn, "1"));
        const headerSiireSakiCd = normalizedValue(DicVal(entryData, HEADER_FIELD_IDS.siireSakiCd, ""));
        const headerSiireSakiSubCd = normalizedValue(DicVal(entryData, HEADER_FIELD_IDS.siireSakiSubCd, "00000"));
        let changed = false;
        nextRows.forEach(row => {
            if (isEmpty(row)) return;

            const rowId = normalizedValue(DicVal(row, "rowid", ""));
            if (rowId === "" || existingRowIds.has(rowId)) {
                return;
            }

            if (normalizedValue(DicVal(row, DETAIL_COLUMN_IDS.hinban, "")) !== "") {
                return;
            }

            // 画面設計書では出荷実績参照で追加した行はカット代として扱います。
            // 追加前の rowid と比較して増えた行だけを補正し、既存の空行や他処理が設定した行は触らないようにします。
            // 旧画面でも素材・附属ともに品番参照はヘッダの仕入先とブランドでマスタ解決しているため、
            // カット代設定の ENV_EX1 を明細仕入先へそのまま入れると単価・単位・税区分を引けません。
            // ここでは追加時点のヘッダ仕入先を明細キーへ設定し、通常の品番入力時と同じ条件で共通取得させます。
            row[DETAIL_COLUMN_IDS.zairyoKbn] = currentPanelType === DETAIL_PANEL_TYPES.material
                ? "1"
                : currentPanelType === DETAIL_PANEL_TYPES.accessory
                    ? "2"
                    : "3";
            row[DETAIL_COLUMN_IDS.hinban] = cutCostContext.cutCostHinban;
            row[DETAIL_COLUMN_IDS.zaiSiireSakiKbn] = headerSiireSakiKbn === "" ? "1" : headerSiireSakiKbn;
            row[DETAIL_COLUMN_IDS.zaiSiireSakiCd] = headerSiireSakiCd;
            row[DETAIL_COLUMN_IDS.zaiSiireSakiSubCd] = headerSiireSakiSubCd === "" ? "00000" : headerSiireSakiSubCd;
            row[DETAIL_COLUMN_IDS.zaiColorCd] = "";
            row[DETAIL_COLUMN_IDS.mekerColorNo] = "";
            row[DETAIL_COLUMN_IDS.cutCostHinban] = cutCostContext.cutCostHinban;
            row[DETAIL_COLUMN_IDS.cutCostSiireSakiCd] = cutCostContext.cutCostSiireSakiCd;
            row[DETAIL_COLUMN_IDS.cutCostBrdCd] = cutCostContext.cutCostBrdCd;
            changedRowIds.push(rowId);
            changed = true;
        });

        if (!changed) {
            return;
        }

        context.ApplyGridData(currentPanelInfo, nextRows);
        context.SyncTotalPanel(currentPanelInfo, nextRows, null);

        const panelColInfos = Array.isArray(context.columninfo)
            ? context.columninfo.filter(column => normalizedValue(DicVal(column, "type", "")) === currentPanelType)
            : [];
        const hinbanColumn = panelColInfos.find(column => normalizedValue(DicVal(column, "columnid", "")) === DETAIL_COLUMN_IDS.hinban);

        if (isEmpty(hinbanColumn)) {
            context.fnc.current.warnmsg("カット代の初期化に必要な品番列定義を取得できません。s_layout を確認してください。");
            return;
        }

        const requestRows = nextRows
            .filter(row => changedRowIds.includes(normalizedValue(DicVal(row, "rowid", ""))))
            .map(row => {
                const requestRow = { ...row };
                if (!isEmpty(context.entrydataRef?.current)) {
                    const mergedRow = { ...requestRow, ...context.entrydataRef.current };
                    if (normalizedValue(DicVal(mergedRow, HEADER_FIELD_IDS.siireSakiKbn, "")) === "") {
                        mergedRow[HEADER_FIELD_IDS.siireSakiKbn] = normalizedValue(DicVal(requestRow, DETAIL_COLUMN_IDS.zaiSiireSakiKbn, "1"));
                    }
                    if (normalizedValue(DicVal(mergedRow, HEADER_FIELD_IDS.siireSakiCd, "")) === "") {
                        mergedRow[HEADER_FIELD_IDS.siireSakiCd] = normalizedValue(DicVal(requestRow, DETAIL_COLUMN_IDS.zaiSiireSakiCd, ""));
                    }
                    if (normalizedValue(DicVal(mergedRow, HEADER_FIELD_IDS.siireSakiSubCd, "")) === "") {
                        mergedRow[HEADER_FIELD_IDS.siireSakiSubCd] = normalizedValue(DicVal(requestRow, DETAIL_COLUMN_IDS.zaiSiireSakiSubCd, "00000"));
                    }
                    return mergedRow;
                }
                return requestRow;
            });

        if (requestRows.length === 0) {
            return;
        }

        // 画面設計書ではカット代も通常の品番入力後と同じ内容で単価などを表示する必要があります。
        // 追加時は setDataValue を通らず品番トリガーが走らないため、ここで既存の行マスタ取得を明示的に呼びます。
        context.fnc.current.getRowMasterdata([hinbanColumn], panelColInfos, requestRows, (data) => {
            try {
                if (!Array.isArray(data) || data.length === 0) {
                    context.fnc.current.warnmsg("カット代の単価取得に失敗しました。品番設定を確認してください。");
                    return;
                }

                const latestRows = context.GridData(currentPanelInfo);
                if (!Array.isArray(latestRows) || latestRows.length === 0) {
                    return;
                }

                const appliedRows = DeepCopy(latestRows);
                let applied = false;
                data.forEach(resultRow => {
                    const resultRowId = normalizedValue(DicVal(resultRow, "rowid", ""));
                    if (resultRowId === "") return;
                    const targetIndex = appliedRows.findIndex(row => normalizedValue(DicVal(row, "rowid", "")) === resultRowId);
                    if (targetIndex === -1) return;

                    for (const key in resultRow) {
                        if (key === "callRecords") continue;
                        appliedRows[targetIndex][key] = resultRow[key];
                    }
                    applied = true;
                });

                if (!applied) {
                    context.fnc.current.warnmsg("カット代の単価取得結果を明細へ反映できませんでした。");
                    return;
                }

                context.ApplyGridData(currentPanelInfo, appliedRows);
                context.SyncTotalPanel(currentPanelInfo, appliedRows, null);

                const targetRow = appliedRows.find(row =>
                    changedRowIds.includes(normalizedValue(DicVal(row, "rowid", ""))));
                if (isEmpty(targetRow)) {
                    context.TriggerEntryInputRender();
                    return;
                }

                const defaultInfoRequest = {
                    rowid: normalizedValue(DicVal(targetRow, "rowid", "")),
                    zairyoKbn: normalizedValue(DicVal(targetRow, DETAIL_COLUMN_IDS.zairyoKbn, "")),
                    torKbn: normalizedValue(DicVal(targetRow, DETAIL_COLUMN_IDS.zaiSiireSakiKbn, "1")),
                    torCd: normalizedValue(DicVal(targetRow, DETAIL_COLUMN_IDS.zaiSiireSakiCd, "")),
                    torSubCd: normalizedValue(DicVal(targetRow, DETAIL_COLUMN_IDS.zaiSiireSakiSubCd, "00000")),
                    brand: normalizedValue(DicVal(isEmpty(context.entrydataRef?.current) ? {} : context.entrydataRef.current, RAW_PURCHASE_ENTRY_FIELD_IDS.brandCode, "")),
                    hinban: normalizedValue(DicVal(targetRow, DETAIL_COLUMN_IDS.hinban, ""))
                };

                if (defaultInfoRequest.zairyoKbn === ""
                    || defaultInfoRequest.torCd === ""
                    || defaultInfoRequest.brand === ""
                    || defaultInfoRequest.hinban === "") {
                    context.TriggerEntryInputRender();
                    return;
                }

                // 画面設計書では出荷実績参照で追加したカット代も、通常の品番入力時と同様に
                // 単価・単位・税区分が入った状態で入力開始する必要があります。
                // 共通の行マスタ取得は名称や一部の参照項目までしか返さないため、
                // この画面だけは追加直後に専用APIでもう一度マスタ既定値を補完します。
                context.fnc.current.postaxios("RawPurchase/GetCutCostRowDefaultInfo",
                    {
                        controller: context.dataController,
                        data: defaultInfoRequest
                    },
                    {
                        onSuccess: function (defaultInfo) {
                            if (!defaultInfo.status) {
                                context.fnc.current.warnmsg(isEmpty(defaultInfo.errDescription)
                                    ? "カット代の既定値取得に失敗しました。"
                                    : defaultInfo.errDescription);
                                context.TriggerEntryInputRender();
                                return;
                            }

                            if (defaultInfo.results !== "OK" || isEmpty(defaultInfo.rowdata)) {
                                context.TriggerEntryInputRender();
                                return;
                            }

                            const latestRows = context.GridData(currentPanelInfo);
                            if (!Array.isArray(latestRows) || latestRows.length === 0) {
                                context.TriggerEntryInputRender();
                                return;
                            }

                            const nextAppliedRows = DeepCopy(latestRows);
                            const targetIndex = nextAppliedRows.findIndex(row =>
                                normalizedValue(DicVal(row, "rowid", "")) === defaultInfoRequest.rowid);
                            if (targetIndex === -1) {
                                context.TriggerEntryInputRender();
                                return;
                            }

                            for (const key in defaultInfo.rowdata) {
                                if (key === "rowid") continue;
                                nextAppliedRows[targetIndex][key] = defaultInfo.rowdata[key];
                            }

                            context.ApplyGridData(currentPanelInfo, nextAppliedRows);
                            recalculateReferenceDetailRows(context, currentPanelInfo);
                            context.TriggerEntryInputRender();
                        },
                        onError: function () {
                            context.fnc.current.warnmsg("カット代の既定値取得に失敗しました。");
                            context.TriggerEntryInputRender();
                        }
                    });
            } finally {
                context.fnc.current.wait(false);
            }
        });
    }, 0);
}

function queryAddOrInsertCutCostRow(context) {
    if (!hasRawPurchaseEntryReference(context)) {
        return true;
    }

    const currentPanelType = panelType(context.panelInfo);
    if (currentPanelType !== DETAIL_PANEL_TYPES.material
        && currentPanelType !== DETAIL_PANEL_TYPES.accessory
        && currentPanelType !== DETAIL_PANEL_TYPES.subMaterial) {
        if (context.rowcount === undefined || context.rowcount === null) {
            context.fnc.current.warnmsg("出荷実績では、この明細を追加できません。");
        }
        return false;
    }

    const cutCostContext = resolveCutCostContext(context);
    if (isEmpty(cutCostContext)) {
        context.fnc.current.warnmsg("カット代の設定値を取得できません。S_ENV.CutCostHinban を確認してください。");
        return false;
    }
    if (cutCostContext.cutCostSiireSakiCd === "" || cutCostContext.cutCostBrdCd === "") {
        context.fnc.current.warnmsg("カット代の設定値を取得できません。S_ENV.CutCostHinban の拡張設定を確認してください。");
        return false;
    }

    const currentRows = context.GridData(context.panelInfo);
    const beforeRowIds = Array.isArray(currentRows)
        ? currentRows.map(row => normalizedValue(DicVal(row, "rowid", ""))).filter(rowId => rowId !== "")
        : [];

    // 画面設計書では出荷実績参照で行追加した明細はカット代として入力開始します。
    // 追加後に増えた rowid だけを特定して補正し、既存行や検索結果の展開とは分けて扱います。
    scheduleNormalizeAddedCutCostRows(context, beforeRowIds, cutCostContext);
    return true;
}

const detailPanelTypesByMaterialCategory = buildRawPurchaseEntryDetailPanelTypes("310", "320", "330");
const entryHooks = {
    applyScreenRules: (context) => {
        applyRawPurchaseEntryReferenceHeaderLocks(context, HEADER_LOCK_COLUMN_IDS);
        if (!hasRawPurchaseEntryReference(context)) return;

        let changed = false;
        if (context.reason === "after-load-new-entry" || context.reason === "after-load-data") {
            context.DetailGridList().forEach(panelInfo => {
                changed = recalculateReferenceDetailRows(context, panelInfo) || changed;
            });
        } else if (context.reason === "base-value-changed"
            && (context.changedColumnId === HEADER_FIELD_IDS.kaigaiKbn || context.changedColumnId === HEADER_FIELD_IDS.shanaiRate)) {
            context.DetailGridList().forEach(panelInfo => {
                changed = recalculateReferenceDetailRows(context, panelInfo) || changed;
            });
        } else if (context.reason === "grid-row-value-changed") {
            changed = recalculateReferenceDetailRows(context, context.panelInfo);
        }

	        if (changed) {
	            // 画面設計書では参照時にレート再計算した結果を明細へ反映するとされているため、
	            // 参照読込時と数量変更時はここで単価・金額を再計算して表示内容を揃えます。
	            context.TriggerEntryInputRender();
	        }
	    },
    gridLockCell: (context) => {
        if (!hasRawPurchaseEntryReference(context)) {
            return context.edit;
        }
        return canEditReferencedRow(context);
    },
    queryAddRow: (context) => {
        return queryAddOrInsertCutCostRow(context);
    },
    queryInsertRow: (context) => {
        return queryAddOrInsertCutCostRow(context);
    },
    queryRemoveRow: () => {
        return true;
    }
};

const entryConfig = {
    screenId: "RawPurchaseEntryRawShipDtl",
    apiController: "RawPurchase",
    dataController: "RawPurchase",
    layoutController: "RawPurchase",
    formKey: "RawPurchaseEntryRawShipDtl",
    listController: "RawShipDtl",
    callerIdentifier: "RawShipDtl",
    sourceCategoryCode: "3",
    primarykeys: ["ZSR_DENNO"],
    sourceCategoryColumnId: RAW_PURCHASE_ENTRY_FIELD_IDS.sourceCategoryCode,
    materialCategoryColumnId: "ZSR_ZAIRYOKBN",
    materialCategoryStateKeys: [
        "materialCategoryCode"
    ],
    detailPanelTypesByMaterialCategory: detailPanelTypesByMaterialCategory,
    hiddenPanelTypesByMaterialCategory: buildRawPurchaseEntryHiddenPanelTypes("310", "320", "330"),
    totalPanelDefinitions: buildRawPurchaseEntryTotalPanelDefinitions("311", "321", "331"),
    entryHooks: entryHooks
};

export const RawPurchaseEntryRawShipDtl = React.forwardRef((props, ref) => {
    return <RawPurchaseEntryBase {...props} ref={ref} entryConfig={entryConfig} />;
});

